import Cookies from "js-cookie";

export const JWT_COOKIE = "jwt";
export const JWT_SHOP_COOKIE = "jwt_shop";

const COOKIE_OPTIONS = { secure: true, sameSite: "strict" } as const;

// Shop bindings are compared case- and whitespace-insensitively, and Fluid
// hostnames like "shop.fluid.app" match the bare checkout shop subdomain.
// This is the single definition of "the same shop" — every shop-scope
// comparison routes through it so the matching rule can only change in one
// place.
export function normalizeShopName(shop?: string | null): string {
  const normalizedShop = shop?.trim().toLowerCase() ?? "";

  return normalizedShop.replace(/\.fluid\.[^.]+$/, "");
}

// The jwt cookie is domain-wide on checkout.fluid.app, so a JWT minted while
// shopping one fluid_shop is visible to every other shop's checkout on the
// same browser. JWTs are company-bound on the backend (sync_customer rejects
// cross-company tokens, isolated companies reject shared-wallet records), so
// presenting a cross-shop JWT produces hard failures instead of a logged-out
// checkout. The companion jwt_shop cookie records which shop a JWT was minted
// for; this decides whether the stored JWT may be used for the shop being
// checked out.
export function jwtUsableForShop({
  boundShop,
  expectedShop,
}: {
  boundShop: string | null | undefined;
  expectedShop: string | null | undefined;
}): boolean {
  // No shop context (management pages, order pages, cart still loading):
  // keep current behavior and let the backend authorize per request.
  if (!expectedShop) return true;

  // A JWT stored before shop binding existed cannot be attributed to a shop,
  // and adopting it into the current shop would recreate the cross-shop leak.
  if (!boundShop) return false;

  return normalizeShopName(boundShop) === normalizeShopName(expectedShop);
}

// Reads the binding cookie and decides whether the stored JWT may be used for
// the given shop. Convenience over jwtUsableForShop for cookie-backed callers.
export function storedJwtUsableForShop(expectedShop?: string | null): boolean {
  return jwtUsableForShop({
    boundShop: Cookies.get(JWT_SHOP_COOKIE),
    expectedShop,
  });
}

export interface JwtShopTokenCandidate {
  fluid_shop?: string | null;
  jwt?: string | null;
}

export function findJwtForShop(
  candidates: readonly JwtShopTokenCandidate[],
  expectedShop?: string | null,
): string | null {
  const normalizedExpectedShop = normalizeShopName(expectedShop);
  if (!normalizedExpectedShop) return null;

  const matchingCandidate = candidates.find(
    (candidate) =>
      normalizeShopName(candidate.fluid_shop) === normalizedExpectedShop &&
      candidate.jwt,
  );

  return matchingCandidate?.jwt ?? null;
}

export async function fetchJwtForShop({
  currentJwt,
  expectedShop,
  fetchCustomerCompanies,
}: {
  currentJwt: string;
  expectedShop?: string | null;
  fetchCustomerCompanies: (
    jwt: string,
  ) => Promise<{ companies: readonly JwtShopTokenCandidate[] }>;
}): Promise<string | null> {
  const { companies } = await fetchCustomerCompanies(currentJwt);

  return findJwtForShop(companies, expectedShop);
}

// Stores the JWT together with its shop binding. A missing shop clears the
// binding so the token reads as unbound (stale to any shop-scoped reader)
// rather than inheriting a previous token's shop.
export function setJwtCookies(jwt: string, shop?: string | null): void {
  Cookies.set(JWT_COOKIE, jwt, COOKIE_OPTIONS);
  if (shop) {
    Cookies.set(JWT_SHOP_COOKIE, shop, COOKIE_OPTIONS);
  } else {
    Cookies.remove(JWT_SHOP_COOKIE);
  }
}

// The token and its binding must always be cleared together — an orphaned
// jwt_shop would mislabel the next login.
export function clearJwtCookies(): void {
  Cookies.remove(JWT_COOKIE);
  Cookies.remove(JWT_SHOP_COOKIE);
}
